import { z } from "zod";

/*
 * The SSH terminal in the browser (docs/terminal.md): a session answer with a
 * one-use ticket, then a WebSocket on our origin; the API is the SSH client.
 */

/** A Unix user name as sshd accepts it (portable POSIX names, at most 32 characters). */
export const UnixUser = z
  .string()
  .trim()
  .regex(/^[a-zA-Z0-9_][a-zA-Z0-9_.-]{0,31}$/, "Letters, digits, dots, hyphens and underscores");

/**
 * How the API signs in to the server: `certificate` — a one-time key signed
 * by the organisation's SSH CA (the server trusts the CA); `password` — the
 * Unix password, typed for this session only and never stored.
 */
export const TerminalAuth = z.enum(["certificate", "password"]);
export type TerminalAuth = z.infer<typeof TerminalAuth>;

export const TerminalSessionRequest = z.object({
  unixUser: UnixUser.default("root"),
  auth: TerminalAuth.default("certificate"),
  /**
   * The host key fingerprint (`SHA256:…`) the person accepted after the
   * server's key changed (e.g. after a rebuild); otherwise the known key must match.
   */
  acceptHostKey: z
    .string()
    .regex(/^SHA256:[A-Za-z0-9+/]{43}$/)
    .optional(),
  /**
   * Back to the same shell after a reload (docs/terminal.md, "After a
   * reload"): the one-time token of the last `ready` frame. Within the grace
   * period the session's shell is kept and re-attached; otherwise `409
   * terminal_resume_expired`, and the page opens a new session.
   */
  resumeToken: z
    .string()
    .regex(/^[\w-]{43}$/)
    .optional(),
});
export type TerminalSessionRequest = z.input<typeof TerminalSessionRequest>;

export const TerminalSession = z.object({
  /** Our id of the session: the certificate's key id, in the audit log and the server's auth log. */
  sessionId: z.uuid(),
  /** Path of the WebSocket on our origin, e.g. `/api/v1/terminal/<ticket>`; one use, 30 s. */
  url: z.string().startsWith("/"),
  /** The session is the kept one of `resumeToken`: the same shell, its output since the drop replayed. */
  resumed: z.boolean().optional(),
});
export type TerminalSession = z.infer<typeof TerminalSession>;

/** What the server page shows to prepare a server for the terminal. */
export const TerminalSetup = z.object({
  /** The organisation's CA keys servers should trust (the signing one first). */
  caPublicKeys: z.array(z.string()),
  /** One shell line that installs them as `TrustedUserCAKeys` and reloads sshd (run as root). */
  installCommand: z.string(),
  /** When a certificate sign-in to this server last worked; null when never. */
  certificateWorkedAt: z.iso.datetime().nullable(),
  /** The server's SSH host key we know (trusted on first use); null before the first connection. */
  hostKeyFingerprint: z.string().nullable(),
});
export type TerminalSetup = z.infer<typeof TerminalSetup>;

/**
 * What the terminal learnt about each server of a project, for the picker:
 * a server whose certificate sign-in has worked is connected to straight away.
 * Servers the terminal never reached are left out.
 */
export const TerminalHost = z.object({
  serverId: z.uuid(),
  /** When a certificate sign-in to this server last worked; null when never. */
  certificateWorkedAt: z.iso.datetime().nullable(),
});
export type TerminalHost = z.infer<typeof TerminalHost>;

export const TerminalHostList = z.object({ hosts: z.array(TerminalHost) });
export type TerminalHostList = z.infer<typeof TerminalHostList>;

/**
 * Whether our API can reach the server's SSH port through its Hetzner
 * firewalls: `open` (no firewall, or a rule allows our address), `blocked`
 * (firewalls applied and none allows it), `unknown` (our outgoing address is
 * not configured and no rule allows everyone), `no_address` (no public IP).
 */
export const TerminalReachability = z.object({
  verdict: z.enum(["open", "blocked", "unknown", "no_address"]),
  port: z.number().int(),
  /** The address the API connects to. */
  address: z.string().nullable(),
  /** Our outgoing addresses (`TERMINAL_EGRESS_IPS`) of the address family used. */
  egressIps: z.array(z.string()),
  /** The firewalls applied to the server, and whether each lets us in. */
  firewalls: z.array(z.object({ id: z.uuid(), name: z.string(), allows: z.boolean() })),
  /**
   * The port is open to the whole internet: no firewall is applied, or a
   * rule allows it from everywhere (the remote desktop warns about it).
   */
  exposed: z.boolean(),
});
export type TerminalReachability = z.infer<typeof TerminalReachability>;

/** Description of the rule the panel adds to let the terminal in. */
export const TERMINAL_RULE_DESCRIPTION = "CloudServer browser terminal";

/** Why a terminal session ended or could not start (sent before the socket closes). */
export const TerminalFailure = z.enum([
  "unreachable",
  "timeout",
  "auth_failed",
  "certificate_not_trusted",
  "host_key_changed",
  "shell_failed",
  "idle",
  "lifetime",
  "closed",
  "expired",
]);
export type TerminalFailure = z.infer<typeof TerminalFailure>;

/** Text frames from the browser; binary frames are keyboard input. */
export const TerminalClientMessage = z.discriminatedUnion("type", [
  /** First message: the terminal's size, and the password in password mode. */
  z.object({
    type: z.literal("start"),
    cols: z.number().int().min(2).max(1000),
    rows: z.number().int().min(1).max(500),
    password: z.string().max(1024).optional(),
  }),
  z.object({
    type: z.literal("resize"),
    cols: z.number().int().min(2).max(1000),
    rows: z.number().int().min(1).max(500),
  }),
  /**
   * The person ends the session on purpose (✕, «Отключиться», a reconnect):
   * closed at once, not kept for a resume.
   */
  z.object({ type: z.literal("end") }),
]);
export type TerminalClientMessage = z.infer<typeof TerminalClientMessage>;

/**
 * Why the assistant stopped working in a terminal session (docs/terminal.md,
 * "Working together with the assistant"): `person` — switched off;
 * `inactive` — 30 minutes without a command or the person's input; `ctrl_c`
 * — the person pressed Ctrl+C; `stopped` — «Стоп» in the chat; `closed` —
 * the session ended; `disabled` — the organisation or the project turned it
 * off; `detached` — the person's page went away (a reload): the shell waits
 * to be resumed without the Autopilot.
 */
export const TerminalPairOffReason = z.enum([
  "person",
  "inactive",
  "ctrl_c",
  "stopped",
  "closed",
  "disabled",
  "detached",
]);
export type TerminalPairOffReason = z.infer<typeof TerminalPairOffReason>;

/**
 * «Доступ Автопилота к терминалу» (docs/assistant.md, "Terminal access";
 * owner's decisions of 2026-10-22), per terminal session, in rising order:
 * `off` — the assistant neither sees nor types into the terminal; `read` —
 * it sees the screen (and the selection) and runs read-only commands, refuses
 * the rest; `moderated` — as read, plus writes through a card (one click) and
 * dangerous commands through a card with a fresh second factor; `auto` —
 * reads and writes run without a click, dangerous commands still need a
 * card with a fresh second factor (the safety floor).
 */
export const TERMINAL_ACCESS_LEVELS = ["off", "read", "moderated", "auto"] as const;
export const TerminalAccessLevel = z.enum(TERMINAL_ACCESS_LEVELS);
export type TerminalAccessLevel = z.infer<typeof TerminalAccessLevel>;

/** The level a person starts with when they have not chosen one. */
export const DEFAULT_TERMINAL_ACCESS: TerminalAccessLevel = "moderated";

/**
 * `PUT`, `POST`, `DELETE …/terminal/sessions/:sessionId/pair`: the session's
 * access level. `on` is `level !== "off"`. `expiresAt`: until when the level
 * stays above off and can change between the levels above off without a new
 * second factor (30 minutes after the last command or input of the person);
 * null at off — every rise from off asks a fresh second factor.
 */
export const TerminalPairState = z.object({
  sessionId: z.uuid(),
  level: TerminalAccessLevel,
  on: z.boolean(),
  expiresAt: z.iso.datetime().nullable(),
  /** The level drops to off after this long without a command or the person's input. */
  inactivityMinutes: z.number().int().positive(),
});
export type TerminalPairState = z.infer<typeof TerminalPairState>;

/** `PUT …/terminal/sessions/:sessionId/pair`: the level to set (at most the organisation's maximum). */
export const SetTerminalAccessRequest = z.object({ level: TerminalAccessLevel });
export type SetTerminalAccessRequest = z.infer<typeof SetTerminalAccessRequest>;

/** Text frames from the API; binary frames are the shell's output. */
export const TerminalServerMessage = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("ready"),
    hostKeyFingerprint: z.string(),
    /**
     * One-time token to come back to this shell after a reload (kept by the
     * page in `sessionStorage` only); a new one with every `ready`. Absent
     * when the installation keeps no sessions (`TERMINAL_RESUME_GRACE_SECONDS=0`).
     */
    resumeToken: z
      .string()
      .regex(/^[\w-]{43}$/)
      .optional(),
    /** The same shell as before the drop; the output since then came first. */
    resumed: z.boolean().optional(),
  }),
  z.object({
    type: z.literal("error"),
    reason: TerminalFailure,
    /** For `host_key_changed`: the key the server offers now. */
    hostKeyFingerprint: z.string().optional(),
  }),
  /**
   * The assistant's pair mode in this session changed: on or off (with why),
   * and the command it is running now (null while none runs).
   */
  z.object({
    type: z.literal("pair"),
    on: z.boolean(),
    /** The session's access level now (`off` when `on` is false). */
    level: TerminalAccessLevel,
    reason: TerminalPairOffReason.nullable(),
    command: z.string().nullable(),
  }),
]);
export type TerminalServerMessage = z.infer<typeof TerminalServerMessage>;
